import dayjs from 'dayjs'
import { reduceRight } from 'lodash'

import { FieldTypes, PaymentValidationType } from 'services/api/PaymentApi/types/common'
import { ISystemListItem } from 'services/api/PaymentApi/types/types'

import {
  countriesFieldName,
  defaultCountryCode,
  paymentNameSeparator,
  replaceUserInfoKey,
  ValidationFieldKey,
} from 'shared/constants/payment'
import { ExtraSchema, IExtraPayload } from 'shared/models/Payments'

// import { validateCPF } from '../cpf/validateCPF'
import { cardValidate } from './validation'

interface IReplaceUserInfo {
  fullName: string
  firstName: string
  lastName: string
  email: string
  // phone: string
  additionSlug: string
}

export const replaceLetters = (text: string | number) => String(text).replace(/(^e)|[^\d]|e/g, '')

export function getValueByUserInfoKey({ userInfo }: { userInfo: IReplaceUserInfo }): Record<string, string | number> {
  return {
    [replaceUserInfoKey.USER_FULL_NAME]: userInfo.fullName,
    [replaceUserInfoKey.USER_FIRST_NAME]: userInfo.firstName,
    [replaceUserInfoKey.USER_LAST_NAME]: userInfo.lastName,
    [replaceUserInfoKey.USER_EMAIL]: userInfo.email,
    // [replaceUserInfoKey.USER_PHONE]: userInfo.phone,
    [replaceUserInfoKey.ADDITIONAL_SLUG]: userInfo.additionSlug,
  }
}

export function getInitialValuesForPaymentFields({
  systemData,
  fields,
  userInfo,
}: {
  systemData?: ISystemListItem
  fields?: ExtraSchema[]
  userInfo: IReplaceUserInfo
}): Record<string, string | number> {
  const convertedFields = (systemData?.fields || fields)?.flat(Infinity)
  const min = systemData?.min ?? 0

  if (!convertedFields) {
    return { amount: min }
  }

  const values = [...convertedFields].reduce((acc, item) => {
    let initialValue: string | number = ''
    if (item.name !== countriesFieldName && item.fieldType === FieldTypes.SELECTOR && item.values.length)
      initialValue = item.values[0].value
    else if (item.name === countriesFieldName) initialValue = defaultCountryCode
    if (item.value && typeof item.value === 'string') {
      const info = getValueByUserInfoKey({ userInfo })
      initialValue = info[item.value] ?? item.value ?? initialValue
    }

    return {
      ...acc,
      [item.name]: initialValue,
    }
  }, {} as Record<string, string | number>)

  return { ...values, amount: min }
}

export function getErrorFieldByValidationType({
  validation,
  value,
}: {
  validation: PaymentValidationType
  value: string | number
}): string | void {
  let error
  // if (validation === ValidationFieldKey.CPF) {
  //   let updatedValue = replaceLetters(value)
  //   if (updatedValue.length < 11) error = 'minDocumentNumber'
  //   else if (!validateCPF(updatedValue)) error = 'invalidCPF'
  // } else
  if (validation === ValidationFieldKey.CARD) {
    let updatedValue = replaceLetters(value)
    if (updatedValue.length !== 16) error = 'cardNumberMustBe'
    else if (!cardValidate(+updatedValue)) error = 'invalidCardNumber'
  }

  return error
}

export function getValidateForPaymentFields({
  values,
  systemData,
}: {
  values: Record<string, string | number>
  systemData?: ISystemListItem
}) {
  const errors = {} as Record<string, string>

  const fields = systemData?.fields.flat(Infinity)

  fields?.forEach(field => {
    if (!field.isOptional && !values[field.name]) {
      errors[field.name] = 'required'
    } else if (
      values[field.name] &&
      field.minLength !== null &&
      field.minLength === field.maxLength &&
      values[field.name].toString().length !== field.minLength
    ) {
      errors[field.name] = 'fieldLengthMustBe'
    } else if (
      values[field.name] &&
      field.minLength !== null &&
      values[field.name].toString().length < field.minLength
    ) {
      errors[field.name] = 'fieldMustBeMoreThan'
    } else if (
      values[field.name] &&
      field.maxLength !== null &&
      values[field.name].toString().length > field.maxLength
    ) {
      errors[field.name] = 'fieldMustBeLessThan'
    } else if (field.validation) {
      // всегда последняя проверка
      const err = getErrorFieldByValidationType({ validation: field.validation, value: values[field.name] })
      if (err) errors[field.name] = err
    }
  })

  if (!String(values.amount).length) {
    errors.amount = 'required'
  } else if (systemData && Number(values.amount) < systemData.min) {
    errors.amount = 'amountMustBeMoreThan'
  } else if (systemData && Number(values.amount) > systemData.max) {
    errors.amount = 'amountMustBeLessThan'
  }

  return errors
}

const getNestedExtraFieldsForTransfer = (values: IExtraPayload[]): IExtraPayload =>
  values.reduce((acc, obj) => {
    // eslint-disable-next-line no-restricted-syntax
    for (const key in obj) {
      if (typeof obj[key] !== 'object') {
        acc[key] = obj[key]
      } else {
        acc[key] = getNestedExtraFieldsForTransfer([acc[key], obj[key]] as IExtraPayload[])
      }
    }
    return acc
  }, {} as IExtraPayload)

export function getFormattedExtraFieldsForTransfer({
  values,
  fields,
  estimatedAmount,
}: {
  values: Record<string, string | number>
  fields: ExtraSchema[]
  estimatedAmount?: string | number
}): { currentAmount: string | number; extra: IExtraPayload } {
  let extraObjects = [] as IExtraPayload[]
  let currentAmount = values.amount
  Object.entries(values).forEach(([key, value]) => {
    const currentField = fields.find(field => key === field.name)
    let updatedValue = value

    if (currentField?.value === replaceUserInfoKey.AMOUNT) {
      updatedValue = values.amount
    }

    if (key !== 'amount') {
      if (
        String(currentField?.value).includes(replaceUserInfoKey.USER_DOCUMENT) ||
        String(currentField?.value).includes(replaceUserInfoKey.USER_PHONE) ||
        String(currentField?.validation).includes(ValidationFieldKey.CPF) ||
        String(currentField?.validation).includes(ValidationFieldKey.CARD)
      ) {
        updatedValue = replaceLetters(updatedValue)
      }

      if (
        String(currentField?.value).includes(replaceUserInfoKey.ESTIMATED_AMOUNT) &&
        estimatedAmount &&
        currentAmount
      ) {
        updatedValue = estimatedAmount
      } else if (String(currentField?.value).includes(replaceUserInfoKey.ESTIMATED_AMOUNT)) {
        updatedValue = '0'
      }

      extraObjects.push(
        reduceRight(
          key.split(paymentNameSeparator),
          (memo, arrayValue) =>
            currentField?.fieldDuplicate
              ? { [arrayValue]: memo, [currentField.fieldDuplicate]: memo }
              : { [arrayValue]: memo },
          updatedValue as unknown as IExtraPayload,
        ),
      )
    }
  })

  return {
    extra: getNestedExtraFieldsForTransfer(extraObjects),
    currentAmount,
  }
}
